import React, { FC, createContext, useCallback, useContext } from 'react'
import { WithChildren } from '../helpers'
import { useIntl } from 'react-intl'

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

type Props = {
  selectedLang: 'de' | 'en' | 'es' | 'fr' | 'ja' | 'zh' | 'pt'
}

export type TFunction = (id: string, values?: Record<string, any>, fallback?: string) => string
export type Gender = 'male' | 'female' | 'other'
export type EntityMeta = {
  gender: Gender
  label: string
  labelLower: string
  plural: string
  pluralLower: string
}

const initialState: Props = {
  selectedLang: 'pt',
}

function getConfig(): Props {
  const ls = localStorage.getItem(I18N_CONFIG_KEY)
  if (ls) {
    try {
      return JSON.parse(ls) as Props
    } catch (er) {
      console.error(er)
    }
  }
  return initialState
}

// Side effect
export function setLanguage(lang: string) {
  localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({ selectedLang: lang }))
  window.location.reload()
}

const I18nContext = createContext<Props>(initialState)

const useLang = () => {
  return useContext(I18nContext).selectedLang
}

const MetronicI18nProvider: FC<WithChildren> = ({ children }) => {
  const lang = getConfig()
  return <I18nContext.Provider value={lang}>{children}</I18nContext.Provider>
}

export const useT = () => {
  const intl = useIntl()

  // Estável entre renders (só muda com o idioma): quem põe `t` em `useMemo`/`useEffect` não
  // recalcula a cada render. Ex.: colunas de tabela feitas com `t` recriavam o `Cell` e
  // desmontavam as células (o estado delas sumia a cada atualização da lista).
  const t: TFunction = useCallback(
    (id, values, fallback) => {
      const msg = intl.formatMessage({ id }, values)
      return msg === id ? fallback ?? id : msg
    },
    [intl]
  )

  return { t }
}

export const getEntityMeta = (t: TFunction, field?: string): EntityMeta => {
  if (!field) {
    return {
      gender: 'male',
      label: '',
      labelLower: '',
      plural: '',
      pluralLower: '',
    }
  }

  return {
    gender: t(`${field}.gender`, undefined, 'male') as Gender,
    label: t(`${field}.label`, undefined, field),
    labelLower: t(`${field}.label.lowercase`, undefined, field),
    plural: t(`${field}.plural`, undefined, field),
    pluralLower: t(`${field}.plural.lowercase`, undefined, field),
  }
}

export { MetronicI18nProvider, useLang }
